import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, User, Calendar, Phone, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MathCaptcha from '../components/MathCaptcha';

import { BACKEND_URL } from '../config';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    dateOfBirth: '',
    mobileNumber: ''
  });
  const [captchaData, setCaptchaData] = useState({
    captchaId: '',
    captchaAnswer: ''
  });
  const [captchaError, setCaptchaError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle date formatting for DD-MM-YYYY
    if (name === 'dateOfBirth') {
      // Auto-format as user types DD-MM-YYYY
      let formattedValue = value.replace(/[^\d]/g, ''); // Remove non-digits
      
      if (formattedValue.length >= 2) {
        formattedValue = formattedValue.slice(0, 2) + '-' + formattedValue.slice(2);
      }
      if (formattedValue.length >= 5) {
        formattedValue = formattedValue.slice(0, 5) + '-' + formattedValue.slice(5, 9);
      }
      
      setFormData({
        ...formData,
        [name]: formattedValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    setError('');
  };

  const handleCaptchaChange = (captchaId, captchaAnswer) => {
    setCaptchaData({ captchaId, captchaAnswer });
    setCaptchaError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CAPTCHA
    if (!captchaData.captchaAnswer) {
      setCaptchaError('Please solve the math problem');
      return;
    }

    // Validate date format DD-MM-YYYY
    const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
    if (!dateRegex.test(formData.dateOfBirth)) {
      setError('Please enter date in DD-MM-YYYY format (e.g., 13-04-1995)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Convert DD-MM-YYYY to YYYY-MM-DD for backend
      const [day, month, year] = formData.dateOfBirth.split('-');
      const isoDate = `${year}-${month}-${day}`;
      
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/forgot-password`,
        {
          ...formData,
          dateOfBirth: isoDate, // Send in YYYY-MM-DD format
          captchaId: captchaData.captchaId,
          captchaAnswer: parseInt(captchaData.captchaAnswer)
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 5000);
      }
    } catch (err) {
      setError(
        err.response?.data?.detail || 
        'Failed to reset password. Please verify your information and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0d6571]/10 mb-4">
                <Lock className="w-8 h-8 text-[#0d6571]" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Forgot Password</h2>
              <p className="mt-2 text-sm text-gray-600">
                Enter your account details to reset your password
              </p>
            </div>

            {/* Success Message */}
            {success ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                  <Mail className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  Password Reset Successful!
                </h3>
                <p className="text-sm text-green-700 mb-4">
                  A new password has been sent to your email address.
                </p>
                <p className="text-xs text-green-600">
                  Redirecting to login page in 5 seconds...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> All fields must match your account information exactly.
                  </p>
                </div>

                {/* Username */}
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="username"
                      name="username"
                      type="text"
                      required
                      value={formData.username}
                      onChange={handleChange}
                      className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0d6571] focus:border-transparent transition-all"
                      placeholder="Enter your username"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0d6571] focus:border-transparent transition-all"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                {/* Date of Birth */}
                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="dateOfBirth"
                      name="dateOfBirth"
                      type="text"
                      required
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      maxLength="10"
                      className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0d6571] focus:border-transparent transition-all"
                      placeholder="DD-MM-YYYY (e.g., 13-04-1995)"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Format: DD-MM-YYYY (Day-Month-Year)</p>
                </div>

                {/* Mobile Number */}
                <div>
                  <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="mobileNumber"
                      name="mobileNumber"
                      type="tel"
                      required
                      value={formData.mobileNumber}
                      onChange={handleChange}
                      className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0d6571] focus:border-transparent transition-all"
                      placeholder="Enter your mobile number"
                    />
                  </div>
                </div>

                {/* CAPTCHA Component */}
                <MathCaptcha 
                  onCaptchaChange={handleCaptchaChange}
                  error={captchaError}
                />

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-[#0d6571] hover:bg-[#0d6571]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0d6571] disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            )}

            {/* Back to Login */}
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-sm font-medium text-[#0d6571] hover:text-[#0d6571]/80 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Link>
            </div>
          </div>

          {/* Additional Help */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Still having trouble?{' '}
              <Link to="/contact" className="font-medium text-[#0d6571] hover:text-[#0d6571]/80">
                Contact Support
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
